import type { AttachmentContent } from '@/services/api';

export interface ProcessedAttachment {
  id: string;
  blob: Blob;
  objectUrl: string;
  mimeType: string;
  fileType: 'image' | 'pdf' | 'document' | 'unknown';
}

const MIME_SIGNATURES: ReadonlyArray<{ signature: number[]; mimeType: string }> = [
  { signature: [0xFF, 0xD8, 0xFF], mimeType: 'image/jpeg' },
  { signature: [0x89, 0x50, 0x4E, 0x47], mimeType: 'image/png' },
  { signature: [0x47, 0x49, 0x46], mimeType: 'image/gif' },
  { signature: [0x52, 0x49, 0x46, 0x46], mimeType: 'image/webp' },
  { signature: [0x25, 0x50, 0x44, 0x46], mimeType: 'application/pdf' },
  // Could be DOCX, XLSX, PPTX, or ZIP
  { signature: [0x50, 0x4B], mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
  // Old Office formats (DOC, XLS, PPT)
  { signature: [0xD0, 0xCF, 0x11, 0xE0], mimeType: 'application/msword' },
];

function matchesSignature(bytes: Uint8Array, signature: number[]): boolean {
  return signature.every((value, index) => bytes[index] === value);
}

/**
 * Detects MIME type from base64 content by checking file signatures (magic numbers)
 */
function detectMimeType(base64Content: string): string {
  try {
    // Decode first few bytes to check file signature
    const binaryString = atob(base64Content.substring(0, 32));
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.codePointAt(i) ?? 0;
    }

    for (const { signature, mimeType } of MIME_SIGNATURES) {
      if (matchesSignature(bytes, signature)) {
        return mimeType;
      }
    }
  } catch (error) {
    console.warn('Failed to detect MIME type:', error);
  }
  
  // Default fallback
  return 'application/octet-stream';
}

/**
 * Determines file type category from MIME type
 */
function getFileType(mimeType: string): 'image' | 'pdf' | 'document' | 'unknown' {
  if (mimeType.startsWith('image/')) {
    return 'image';
  }
  if (mimeType === 'application/pdf') {
    return 'pdf';
  }
  if (mimeType.includes('document') || mimeType.includes('word') || 
      mimeType.includes('sheet') || mimeType.includes('presentation') ||
      mimeType.includes('text/')) {
    return 'document';
  }
  return 'unknown';
}

/**
 * Processes base64 encoded attachment content into usable blobs and object URLs
 */
export function processAttachmentContents(attachmentContents: AttachmentContent[]): {
  imageBlobs: Blob[];
  imageUrls: string[];
  allAttachments: ProcessedAttachment[];
} {
  console.log('🔄 Processing attachment contents...');

  const processedAttachments = attachmentContents.map(att => {
    try {
      // Detect MIME type from content
      const mimeType = detectMimeType(att.encoded_content);
      const fileType = getFileType(mimeType);
      
      console.log(`📎 Processing attachment ${att.id}: ${mimeType} (${fileType})`);

      // Convert base64 to blob and create object URL
      const binaryString = atob(att.encoded_content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.codePointAt(i) ?? 0;
      }
      const blob = new Blob([bytes], { type: mimeType });
      const objectUrl = URL.createObjectURL(blob);
      
      return {
        id: att.id,
        blob,
        objectUrl,
        mimeType,
        fileType
      };
    } catch (error) {
      console.error(`Failed to process attachment ${att.id}:`, error);
      return null;
    }
  }).filter(Boolean) as ProcessedAttachment[];

  // Separate images for backward compatibility
  const imageAttachments = processedAttachments.filter(att => att.fileType === 'image');
  const imageBlobs = imageAttachments.map(img => img.blob);
  const imageUrls = imageAttachments.map(img => img.objectUrl);

  console.log('✅ Processed attachments:', {
    total: processedAttachments.length,
    images: imageAttachments.length,
    pdfs: processedAttachments.filter(att => att.fileType === 'pdf').length,
    documents: processedAttachments.filter(att => att.fileType === 'document').length,
    unknown: processedAttachments.filter(att => att.fileType === 'unknown').length
  });

  return { 
    imageBlobs, 
    imageUrls, 
    allAttachments: processedAttachments 
  };
}

/**
 * Clean up object URLs to prevent memory leaks
 */
export function cleanupImageUrls(imageUrls: string[]): void {
  imageUrls.forEach(url => {
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      console.warn('Failed to revoke object URL:', url, error);
    }
  });
}

/**
 * Clean up all attachment URLs (including non-images)
 */
export function cleanupAttachmentUrls(attachments: ProcessedAttachment[]): void {
  attachments.forEach(attachment => {
    try {
      URL.revokeObjectURL(attachment.objectUrl);
    } catch (error) {
      console.warn('Failed to revoke object URL:', attachment.objectUrl, error);
    }
  });
}

/**
 * Get a human-readable file type description
 */
export function getFileTypeDescription(attachment: ProcessedAttachment): string {
  switch (attachment.fileType) {
    case 'image':
      return 'Image';
    case 'pdf':
      return 'PDF Document';
    case 'document':
      if (attachment.mimeType.includes('word')) return 'Word Document';
      if (attachment.mimeType.includes('sheet')) return 'Spreadsheet';
      if (attachment.mimeType.includes('presentation')) return 'Presentation';
      return 'Document';
    default:
      return 'File';
  }
}

/**
 * Check if an attachment can be displayed inline (images)
 */
export function canDisplayInline(attachment: ProcessedAttachment): boolean {
  return attachment.fileType === 'image';
}

/**
 * Check if an attachment can be downloaded
 */
export function canDownload(_attachment: ProcessedAttachment): boolean {
  return true; // All attachments can be downloaded
}
